import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import affiliationService from '../../services/affiliationService';
import { mapAffiliationToOption } from '../../utils/affiliationHelpers';
import './AffiliationBadge.css';

const EMPTY = {
  code: '',
  name: '',
  city: '',
  description: ''
};

export default function CreateSmieAffiliationModal({ show, onHide, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (saving) return;
    setForm(EMPTY);
    setError('');
    onHide();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'code' ? value.toUpperCase().replace(/[^A-Z0-9_]/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const code = form.code.trim();
    const name = form.name.trim();
    if (!code || code.length < 2) {
      setError('Le code doit contenir au moins 2 caractères (lettres/chiffres).');
      return;
    }
    if (!name || name.length < 2) {
      setError('Le nom est obligatoire (2 caractères minimum).');
      return;
    }

    try {
      setSaving(true);
      const res = await affiliationService.create({
        code,
        name,
        city: form.city.trim() || null,
        description: form.description.trim() || null,
        is_active: true
      });
      const created = res?.data;
      if (!res?.success || !created?.id) {
        throw new Error(res?.message || 'Création impossible');
      }
      onCreated?.(mapAffiliationToOption(created));
      handleClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      className="co-aff-create-modal"
      backdropClassName="co-aff-create-backdrop"
      container={typeof document !== 'undefined' ? document.body : undefined}
      enforceFocus
    >
      <Modal.Header closeButton={!saving} className="co-aff-create-modal__header">
        <Modal.Title>Nouvel organisme SMIE</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Cet organisme sera ajouté au référentiel et pourra être rattaché à toutes les sociétés.
          </p>
          {error ? (
            <Alert variant="danger" className="py-2 small">
              {error}
            </Alert>
          ) : null}
          <Form.Group className="mb-3">
            <Form.Label>Code *</Form.Label>
            <Form.Control
              name="code"
              value={form.code}
              onChange={handleChange}
              placeholder="Ex. FUNNOU"
              maxLength={32}
              required
              autoFocus
            />
            <Form.Text className="text-muted">Identifiant court unique (majuscules).</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Nom affiché *</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Nom de l'organisme"
              maxLength={100}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ville</Form.Label>
            <Form.Control
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="Ex. Mahajanga"
              maxLength={100}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={form.description}
              onChange={handleChange}
              maxLength={255}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Création…
              </>
            ) : (
              "Créer l'organisme"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
